import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function EmailOverviewPanel({ refreshKey }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchStats() {
    try {
      const res = await apiFetch('/api/emails/stats');
      if (res.ok) setStats(await res.json());
    } catch {}
    finally { setLoading(false); }
  }

  if (loading) return <section className="card"><h2>Email Overview</h2><p>Loading...</p></section>;
  if (!stats) return <section className="card"><h2>Email Overview</h2><p style={{ color: '#71717a' }}>Unable to load stats.</p></section>;


  async function triggerBackfill() {
    setFetching(true);
    setFetchResult(null);
    try {
      await apiFetch('/api/emails/backfill-trigger', { method: 'POST' });
      setFetchResult({ success: true, message: 'Backfill triggered. Check Activity Log for progress.' });
    } catch (err) {
      setFetchResult({ success: false, message: err.message });
    } finally {
      setFetching(false);
      setTimeout(() => setFetchResult(null), 8000);
    }
  }

  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Email Overview</h2>
        {stats.missingBody > 0 && (
          <button className="manage-btn" onClick={triggerBackfill} disabled={fetching} style={{ fontSize: 12 }}>
            {fetching ? 'Triggering...' : 'Run Backfill Now'}
          </button>
        )}
      </div>
      {fetchResult && (
        <div style={{ fontSize: 12, marginBottom: 8, padding: '6px 10px', borderRadius: 6, background: fetchResult.success ? '#16332a' : '#331616', color: fetchResult.success ? '#22c55e' : '#f87171' }}>
          {fetchResult.message}
        </div>
      )}

      {/* Top stats */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat value={stats.total} label="Total Emails" color="#e4e4e7" />
        <Stat value={stats.withBody} label="With Body" color="#22c55e" />
        <Stat value={stats.missingBody} label="Missing Body" color={stats.missingBody > 0 ? '#fbbf24' : '#22c55e'} />
        <Stat value={`${stats.bodyPercent}%`} label="Bodies Complete" color="#8b5cf6" />
        <Stat value={stats.withAttachments} label="With Attachments" color="#3b82f6" />
      </div>

      {/* Progress bar */}
      <div style={{ background: '#1a1b23', borderRadius: 6, height: 10, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{
          width: `${stats.bodyPercent}%`,
          height: '100%',
          background: stats.bodyPercent >= 100 ? '#22c55e' : '#8b5cf6',
          borderRadius: 6,
          transition: 'width 0.5s ease',
        }} />
      </div>

      {/* By status */}
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 13, color: '#a1a1aa', margin: '0 0 6px 0' }}>By Status</h3>
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <div key={status} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13, padding: '2px 0' }}>
              <span style={{ color: '#e4e4e7', textTransform: 'capitalize' }}>{status.replace(/_/g, ' ')}</span>
              <span style={{ color: '#a1a1aa' }}>{count}</span>
            </div>
          ))}
        </div>

        <div>
          <h3 style={{ fontSize: 13, color: '#a1a1aa', margin: '0 0 6px 0' }}>By Account</h3>
          {Object.entries(stats.byAccount).map(([account, count]) => (
            <div key={account} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13, padding: '2px 0' }}>
              <span style={{ color: '#e4e4e7' }}>{account}</span>
              <span style={{ color: '#a1a1aa' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Date range */}
      {(stats.oldest || stats.newest) && (
        <div style={{ fontSize: 12, color: '#71717a', borderTop: '1px solid #2a2b35', paddingTop: 8 }}>
          {stats.oldest && <span>Oldest: {new Date(stats.oldest).toLocaleDateString()}</span>}
          {stats.oldest && stats.newest && <span> — </span>}
          {stats.newest && <span>Newest: {new Date(stats.newest).toLocaleDateString()}</span>}
        </div>
      )}
    </section>
  );
}

function Stat({ value, label, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#71717a' }}>{label}</div>
    </div>
  );
}

export default EmailOverviewPanel;
